import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Create } from "@refinedev/mui";
import {
  Box,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Alert,
  Chip,
  InputAdornment,
  Paper,
  useTheme,
  alpha,
  CircularProgress,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import {
  IResourceComponentsProps,
  useNavigation,
  useTranslate,
  useNotification,
} from "@refinedev/core";
import {
  PersonAdd,
  Email,
  Business,
  Save,
  LinkOutlined,
} from '@mui/icons-material';
import axiosInstance from "../../api/axios-config";
import { Controller } from "react-hook-form";
import { useAuth } from "../../contexts/auth-context";
import { PhoneNumberInput } from "../../components/common/PhoneInput";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useSearchParams } from "react-router-dom";

export const UserCreate: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const { goBack } = useNavigation();
  const { open } = useNotification();
  const [searchParams] = useSearchParams();

  // Get URL parameters for pre-filling
  const urlEntrepriseId = searchParams.get("entrepriseId");
  const urlRole = searchParams.get("role");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const [currentUserCompany, setCurrentUserCompany] = useState<any>(null);
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [prefilledCompany, setPrefilledCompany] = useState<any>(null);

  // State for email detailed check (linking logic)
  const [emailCheckResult, setEmailCheckResult] = useState<{
    exists: boolean;
    canLink: boolean;
    hasCompany: boolean;
    fullname?: string;
  } | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // Roles that can select enterprise
  const canSelectEnterprise = userRole && ["ADMIN", "SAFEE_MODERATOR", "SAFEE_STAFF"].includes(userRole);
  // Roles that can select user role
  const canSelectRole = userRole === "ADMIN";

  // Watch form values to enable/disable save button
  const watchFullname = watch("fullname");
  const watchEmail = watch("email");
  const watchRole = watch("role");

  // Check if required fields are filled
  const isFormValid = useMemo(() => {
    // If email check shows user already has a company, form is invalid
    if (emailCheckResult?.hasCompany) {
      return false;
    }

    // When linking, fullname is optional (user already has one)
    const isLinking = emailCheckResult?.canLink;
    const hasBasicFields = isLinking
      ? watchEmail // When linking, only email is required
      : watchFullname && watchEmail; // When creating, both are required

    // For ADMIN users, role is required
    if (canSelectRole) {
      return hasBasicFields && watchRole;
    }

    return hasBasicFields;
  }, [watchFullname, watchEmail, watchRole, canSelectRole, emailCheckResult]);

  const generateSecurePassword = (length: number = 12): string => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%&*';
    const allChars = lowercase + uppercase + numbers + symbols;

    // Ensure at least one of each type
    let password = [
      lowercase[Math.floor(Math.random() * lowercase.length)],
      uppercase[Math.floor(Math.random() * uppercase.length)],
      numbers[Math.floor(Math.random() * numbers.length)],
      symbols[Math.floor(Math.random() * symbols.length)],
    ];

    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password.push(allChars[Math.floor(Math.random() * allChars.length)]);
    }

    // Shuffle the password
    return password.sort(() => Math.random() - 0.5).join('');
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const { data: result } = await axiosInstance.get('/auth/me');
        setUserRole(result.role);

        if (result.entrepriseId) {
          const { data: companyData } = await axiosInstance.get(
            `/company/${result.entrepriseId}`
          );
          setCurrentUserCompany(companyData);
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    fetchUserInfo();
  }, []);

  useEffect(() => {
    const fetchAllCompanies = async () => {
      try {
        const { data } = await axiosInstance.get("/company");
        setAllCompanies(data);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    // Fetch companies for users who can select them (ADMIN, SAFEE_MODERATOR, SAFEE_STAFF)
    if (userRole && ["ADMIN", "SAFEE_MODERATOR", "SAFEE_STAFF"].includes(userRole)) {
      fetchAllCompanies();
    }
  }, [userRole]);

  // Pre-fill form with URL parameters when companies are loaded
  useEffect(() => {
    // Pre-fill enterprise from URL when companies are loaded
    if (urlEntrepriseId && allCompanies.length > 0 && canSelectEnterprise) {
      const entrepriseIdNum = parseInt(urlEntrepriseId, 10);
      const selectedCompany = allCompanies.find(c => c.id === entrepriseIdNum);
      if (selectedCompany) {
        setValue("entrepriseId", selectedCompany);
        setPrefilledCompany(selectedCompany);
      }
    }
  }, [urlEntrepriseId, allCompanies, canSelectEnterprise, setValue]);

  // Pre-fill role from URL parameter (only for ADMIN who can select role)
  useEffect(() => {
    if (urlRole && canSelectRole) {
      const validRoles = [
        "USER", "GUEST", "SUB_MODERATOR", "MODERATOR", "MODERATOR_FREE",
        "SAFEE_MODERATOR", "SAFEE_STAFF", "VIGILANE", "ADMIN"
      ];
      if (validRoles.includes(urlRole)) {
        setValue("role", urlRole);
      }
    }
  }, [urlRole, canSelectRole, setValue]);

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const checkUserExists = async (value: string, type: "email" | "phone") => {
    try {
      const response = await axiosInstance.get('/users/check', {
        params: { [type]: value }
      });
      return response.data.exists;
    } catch (error) {
      return false;
    }
  };

  // Check user detailed info for linking logic
  const checkUserDetailed = async (email: string) => {
    try {
      const response = await axiosInstance.get('/users/check-detailed', {
        params: { email }
      });
      return response.data;
    } catch (error) {
      return { exists: false, canLink: false, hasCompany: false };
    }
  };

  // Debounced email check for detailed info using useRef for timeout
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedCheckEmail = useCallback((email: string) => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (!email || !isValidEmail(email)) {
      setEmailCheckResult(null);
      setIsCheckingEmail(false);
      return;
    }

    setIsCheckingEmail(true);

    debounceTimeoutRef.current = setTimeout(async () => {
      const result = await checkUserDetailed(email);
      setEmailCheckResult(result);
      setIsCheckingEmail(false);

      // If user exists and can be linked, pre-fill the fullname
      if (result.exists && result.canLink && result.fullname) {
        setValue("fullname", result.fullname);
      }
    }, 500);
  }, [setValue]);

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);

    try {
      // Clean up phone field - if empty, don't send it
      if (data.phone === '' || data.phone === null) {
        delete data.phone;
      }

      // Generate a secure random password (user will receive it by email)
      const generatedPassword = generateSecurePassword(12);

      // Determine the role: use form data if available, otherwise use URL parameter
      let role = data.role;
      if (!role && urlRole) {
        const validRoles = [
          "USER", "GUEST", "SUB_MODERATOR", "MODERATOR", "MODERATOR_FREE",
          "SAFEE_MODERATOR", "SAFEE_STAFF", "VIGILANE", "ADMIN"
        ];
        if (validRoles.includes(urlRole)) {
          role = urlRole;
        }
      }

      let dataFormatted = {
        ...data,
        password: generatedPassword,
        role: role || undefined,
        entrepriseId: canSelectEnterprise
          ? data.entrepriseId?.id || null
          : prefilledCompany?.id || currentUserCompany?.id || null,
      };

      await axiosInstance.post('/users/create-user', dataFormatted);

      open?.({
        type: "success",
        message: translate('user.create.success'),
        description: translate('user.create.successDesc'),
      });

      goBack();
    } catch (error: any) {
      open?.({
        type: "error",
        message: translate('user.create.error'),
        description: error.response?.data?.message || translate('common.error.unknown'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Create
      title={translate('user.create.title')}
      breadcrumb={false}
      footerButtons={<></>}
      wrapperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                  {translate('user.create.steps.personalInfo')}
                </Typography>

                <Stack spacing={3}>
                  {/* Personal Info Section */}
                  <TextField
                    {...register("fullname", {
                      required: emailCheckResult?.canLink
                        ? false // Optional when linking - user already has a name
                        : translate('user.create.fields.fullnameRequired'),
                    })}
                    error={!!errors?.fullname}
                    helperText={errors?.fullname?.message as string}
                    fullWidth
                    label={translate('user.create.fields.fullname')}
                    placeholder={translate('user.create.fields.fullnamePlaceholder')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonAdd />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    {...register("email", {
                      required: translate('user.create.fields.emailRequired'),
                      validate: {
                        validFormat: (value) =>
                          isValidEmail(value) || translate('user.create.fields.emailInvalid'),
                        emailNotExistsWithCompany: async (value) => {
                          if (!isValidEmail(value)) return true;
                          // Trigger detailed check
                          debouncedCheckEmail(value);
                          // Use detailed check - only reject if user exists AND has a company
                          const result = await checkUserDetailed(value);
                          if (result.exists && result.hasCompany) {
                            return translate('user.create.fields.emailExistsWithCompany');
                          }
                          return true;
                        },
                      },
                    })}
                    error={!!errors?.email}
                    helperText={errors?.email?.message as string}
                    fullWidth
                    type="email"
                    label={translate('user.create.fields.email')}
                    placeholder={translate('user.create.fields.emailPlaceholder')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email />
                        </InputAdornment>
                      ),
                      endAdornment: isCheckingEmail ? (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      ) : null,
                    }}
                  />

                  <Controller
                    name="phone"
                    control={control}
                    rules={{
                      validate: {
                        validFormat: (value) =>
                          !value || isValidPhoneNumber(value) || translate('user.create.fields.phoneInvalid'),
                        phoneNotExists: async (value) => {
                          if (!value || !isValidPhoneNumber(value)) return true;
                          const exists = await checkUserExists(value, "phone");
                          return !exists || translate('user.create.fields.phoneExists');
                        },
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <PhoneNumberInput
                        {...field}
                        label={translate('user.create.fields.phone')}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        fullWidth
                        margin="normal"
                      />
                    )}
                  />

                  {/* Alert: User exists with company (error) */}
                  {emailCheckResult?.hasCompany && (
                    <Alert severity="error">
                      {translate('user.create.fields.emailExistsWithCompany')}
                    </Alert>
                  )}

                  {/* Alert: User can be linked (info) */}
                  {emailCheckResult?.canLink && (
                    <Alert
                      severity="info"
                      icon={<LinkOutlined />}
                      sx={{ '& .MuiAlert-icon': { alignItems: 'center' } }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        {translate('user.create.linking.title')}
                      </Typography>
                      <Typography variant="body2">
                        {translate('user.create.linking.description', {
                          fullname: emailCheckResult.fullname || translate('common.unknown'),
                        })}
                      </Typography>
                    </Alert>
                  )}

                  {/* Info about password - different message when linking */}
                  <Alert severity="info">
                    {emailCheckResult?.canLink
                      ? translate('user.create.security.linkingInfo')
                      : translate('user.create.security.passwordInfo')}
                  </Alert>

                  {/* Organization Section */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      {translate('user.create.steps.organization')}
                    </Typography>

                    <Stack spacing={3}>
                      {/* For MODERATOR: show their company (read-only, auto-assigned) */}
                      {userRole === "MODERATOR" && currentUserCompany && (
                        <>
                          <Alert severity="info">
                            {translate('user.create.organization.autoAdd')}
                          </Alert>
                          <Paper
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Business color="primary" />
                              <Box>
                                <Typography variant="subtitle2">{translate('user.create.organization.label')}</Typography>
                                <Typography variant="h6">{currentUserCompany.nom}</Typography>
                              </Box>
                            </Stack>
                          </Paper>
                        </>
                      )}

                      {/* For users with enterprise pre-filled from URL: show read-only */}
                      {canSelectEnterprise && prefilledCompany && (
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Business color="primary" />
                            <Box>
                              <Typography variant="subtitle2">{translate('user.create.organization.label')}</Typography>
                              <Typography variant="h6">{prefilledCompany.nom}</Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      )}

                      {/* For users who can select enterprise (ADMIN, SAFEE_MODERATOR, SAFEE_STAFF) - only if no prefilled company */}
                      {canSelectEnterprise && !prefilledCompany && (
                        <Controller
                          control={control}
                          name="entrepriseId"
                          render={({ field }) => (
                            <TextField
                              select
                              fullWidth
                              label={translate('user.create.fields.company')}
                              error={!!errors?.entrepriseId}
                              helperText={errors?.entrepriseId?.message as string}
                              value={field.value?.id || ''}
                              onChange={(e) => {
                                const selectedCompany = e.target.value ? allCompanies.find(c => c.id === e.target.value) : null;
                                field.onChange(selectedCompany);
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Business />
                                  </InputAdornment>
                                ),
                              }}
                            >
                              <MenuItem value="">
                                <em>{translate('user.create.fields.companyOptional')}</em>
                              </MenuItem>
                              {allCompanies.map((company) => (
                                <MenuItem key={company.id} value={company.id}>
                                  {company.nom}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      )}

                      {/* Role selector - only for ADMIN */}
                      {canSelectRole && (
                        <Controller
                          control={control}
                          name="role"
                          rules={{
                            required: translate('user.create.fields.roleRequired'),
                          }}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              label={translate('user.create.fields.role')}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              value={field.value || ""}
                            >
                              <MenuItem value="USER">
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Chip label="USER" size="small" />
                                  <Typography variant="caption">{translate('user.create.roles.user')}</Typography>
                                </Stack>
                              </MenuItem>
                              <MenuItem value="GUEST">
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Chip label="GUEST" size="small" />
                                  <Typography variant="caption">{translate('user.create.roles.guest')}</Typography>
                                </Stack>
                              </MenuItem>
                              <MenuItem value="SUB_MODERATOR">
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Chip label="SUB_MODERATOR" size="small" color="info" />
                                  <Typography variant="caption">{translate('user.create.roles.subModerator')}</Typography>
                                </Stack>
                              </MenuItem>
                              <MenuItem value="MODERATOR">
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Chip label="MODERATOR" size="small" color="primary" />
                                  <Typography variant="caption">{translate('user.create.roles.moderator')}</Typography>
                                </Stack>
                              </MenuItem>
                              <MenuItem value="MODERATOR_FREE">
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Chip label="MODERATOR_FREE" size="small" color="primary" />
                                  <Typography variant="caption">{translate('user.create.roles.moderatorFree')}</Typography>
                                </Stack>
                              </MenuItem>
                              <MenuItem value="SAFEE_MODERATOR">
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Chip label="SAFEE_MODERATOR" size="small" color="warning" />
                                  <Typography variant="caption">{translate('user.create.roles.safeeModerator')}</Typography>
                                </Stack>
                              </MenuItem>
                              <MenuItem value="SAFEE_STAFF">
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Chip label="SAFEE_STAFF" size="small" color="warning" />
                                  <Typography variant="caption">{translate('user.create.roles.safeeStaff')}</Typography>
                                </Stack>
                              </MenuItem>
                              <MenuItem value="VIGILANE">
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Chip label="VIGILANE" size="small" color="error" />
                                  <Typography variant="caption">{translate('user.create.roles.vigilane')}</Typography>
                                </Stack>
                              </MenuItem>
                              <MenuItem value="ADMIN">
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Chip label="ADMIN" size="small" color="secondary" />
                                  <Typography variant="caption">{translate('user.create.roles.admin')}</Typography>
                                </Stack>
                              </MenuItem>
                            </TextField>
                          )}
                        />
                      )}
                    </Stack>
                  </Box>

                  {/* Save Button */}
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={!isFormValid || isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
                      sx={{
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                        px: 4,
                        py: 1.5,
                      }}
                    >
                      {translate('user.create.submit')}
                    </Button>
                  </Box>
                </Stack>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Create>
  );
};
